import React, { Suspense } from "react";
import "./index.css";
import "./themes/themes.css";
import { useApariencia, TemaPreviewBar } from "./themes/Apariencia";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { ScopeProvider } from "./lib/scope";
import Layout from "./components/Layout";
import { Toaster } from "./components/ui/sonner";

// React.lazy con reintento: si un chunk falla al cargar (típico tras un redespliegue
// cuando el navegador tiene cacheado un index.html antiguo), recarga la página una vez
// en lugar de quedarse colgado indefinidamente en el fallback de Suspense.
function lazyWithRetry(factory) {
  return React.lazy(() =>
    factory().catch((err) => {
      const KEY = "gmao_chunk_reloaded";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    })
  );
}

const Login = lazyWithRetry(() => import("./pages/Login"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Companies = lazyWithRetry(() => import("./pages/Companies"));
const CompanyDetail = lazyWithRetry(() => import("./pages/CompanyDetail"));
const Installations = lazyWithRetry(() => import("./pages/Installations"));
const InstallationDetail = lazyWithRetry(() => import("./pages/InstallationDetail"));
const Elements = lazyWithRetry(() => import("./pages/Elements"));
const ElementDetail = lazyWithRetry(() => import("./pages/ElementDetail"));
const Incidents = lazyWithRetry(() => import("./pages/Incidents"));
const Revisiones = lazyWithRetry(() => import("./pages/Revisiones"));
const Ofertas = lazyWithRetry(() => import("./pages/Ofertas"));
const Facturacion = lazyWithRetry(() => import("./pages/Facturacion"));
const Alta = lazyWithRetry(() => import("./pages/Alta"));
const Usuarios = lazyWithRetry(() => import("./pages/Usuarios"));
const Etiquetas = lazyWithRetry(() => import("./pages/Etiquetas"));
const WorkOrders = lazyWithRetry(() => import("./pages/WorkOrders"));
const Configuracion = lazyWithRetry(() => import("./pages/Configuracion"));
const Urgencias = lazyWithRetry(() => import("./pages/Urgencias"));
const Asistente = lazyWithRetry(() => import("./pages/Asistente"));
const Historicos = lazyWithRetry(() => import("./pages/Historicos"));
const PublicAsset = lazyWithRetry(() => import("./pages/PublicAsset"));
const PublicQuote = lazyWithRetry(() => import("./pages/PublicQuote"));

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-sm" data-testid="page-loading">Cargando…</div>
);

class RouteErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error) { if (process.env.NODE_ENV === "development") console.error("Route error:", error); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="route-error">
          <div className="text-slate-900 font-bold text-lg">Se ha producido un problema al cargar esta sección</div>
          <p className="text-slate-500 text-sm max-w-md">Puede deberse a una actualización reciente de la aplicación. Vuelve a cargar la página para continuar.</p>
          <button onClick={() => { sessionStorage.removeItem("gmao_chunk_reloaded"); window.location.reload(); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-sm text-sm font-medium hover:bg-blue-700" data-testid="route-error-reload">
            Recargar la aplicación
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function defaultPath(role) {
  if (role === "operator") return "/revisiones";
  if (role === "gestor") return "/empresas";
  if (role === "client_group" || role === "client_plant") return "/empresas";
  return "/";
}

const R_ADMIN = ["admin"];
const R_OP = ["admin", "gestor", "operator"];
const R_ALL5 = ["admin", "gestor", "operator", "client_group", "client_plant"];
const R_ORDERS = ["admin", "operator", "client_group", "client_plant"];

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-sm">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={defaultPath(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();
  useApariencia();
  const home = user ? defaultPath(user.role) : "/";
  React.useEffect(() => { sessionStorage.removeItem("gmao_chunk_reloaded"); }, []);
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={home} replace /> : <Login />} />
      <Route path="/activo/:id" element={<PublicAsset />} />
      <Route path="/oferta/:token" element={<PublicQuote />} />
      <Route path="/" element={<Protected roles={R_OP}><Dashboard /></Protected>} />
      <Route path="/empresas" element={<Protected roles={R_ALL5}><Companies /></Protected>} />
      <Route path="/empresas/:id" element={<Protected roles={R_ALL5}><CompanyDetail /></Protected>} />
      <Route path="/instalaciones" element={<Protected roles={R_OP}><Installations /></Protected>} />
      <Route path="/urgencias" element={<Protected roles={R_OP}><Urgencias /></Protected>} />
      <Route path="/instalaciones/:id" element={<Protected roles={R_ALL5}><InstallationDetail /></Protected>} />
      <Route path="/elementos" element={<Protected roles={R_ALL5}><Elements /></Protected>} />
      <Route path="/elementos/:id" element={<Protected roles={R_ALL5}><ElementDetail /></Protected>} />
      <Route path="/incidencias" element={<Protected roles={R_ALL5}><Incidents /></Protected>} />
      <Route path="/revisiones" element={<Protected roles={R_ALL5}><Revisiones /></Protected>} />
      <Route path="/ordenes" element={<Protected roles={R_ORDERS}><WorkOrders /></Protected>} />
      <Route path="/ofertas" element={<Protected roles={R_ADMIN}><Ofertas /></Protected>} />
      <Route path="/facturacion" element={<Protected roles={R_ADMIN}><Facturacion /></Protected>} />
      <Route path="/alta" element={<Protected roles={["admin", "gestor"]}><Alta /></Protected>} />
      <Route path="/etiquetas" element={<Protected roles={R_ADMIN}><Etiquetas /></Protected>} />
      <Route path="/usuarios" element={<Protected roles={R_ADMIN}><Usuarios /></Protected>} />
      <Route path="/visitas" element={<Navigate to="/configuracion#visitas" replace />} />
      <Route path="/asistente" element={<Protected roles={["admin"]}><Asistente /></Protected>} />
      <Route path="/historicos" element={<Protected roles={["admin"]}><Historicos /></Protected>} />
      <Route path="/auditoria" element={<Navigate to="/configuracion#auditoria" replace />} />
      <Route path="/configuracion" element={<Protected roles={R_ADMIN}><Configuracion /></Protected>} />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScopeProvider>
          <RouteErrorBoundary>
            <Suspense fallback={<PageFallback />}>
              <AppRoutes />
            </Suspense>
            <TemaPreviewBar />
          </RouteErrorBoundary>
        </ScopeProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
